import style from './article-preview-large.module.scss';
import { IArticle } from '@models/article.interface';
import Link from 'next/link';
import Image from 'next/image';
import ReadingTimeBlock from '@components/blog/reading-time-block/reading-time';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface ArticlePreviewLargeProps {
    article: IArticle[];
}

const ArticlePreviewLarge = ({ article }: ArticlePreviewLargeProps) => {
    const MAX_SLIDES = 2;
    const FIRST_SLIDE = 0;
    const NULL_PERCENT = '0%';
    const ONE_HUNDRED_PERCENT = '100%';

    const [allSlides, setAllSlides] = useState<IArticle[]>([]);
    const [currentSlide, setCurrentSlide] = useState<IArticle>();
    const [currentSlideIndex, setCurrentSlideIndex] = useState(FIRST_SLIDE);
    const animatedDivRefs = useRef<(HTMLDivElement | null)[]>([]);
    const WINDOW_VISIBLE = 'visible';
    const WINDOW_HIDDEN = 'hidden';
    const ANIMATION_RUN = 'running';
    const ANIMATION_PAUSED = 'paused';
    const ANIMATION_END = 'animationend';
    const VISIBILITY_CHANGE = 'visibilitychange';
    const [hasTopicEffectRun, setHasTopicEffectRun] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const elements: (HTMLDivElement | null)[] = animatedDivRefs.current;

    useEffect(() => {
        const firstSlides = article.slice(0, MAX_SLIDES + 1);
        setAllSlides(firstSlides);
        setCurrentSlide(firstSlides[0]);
        setHasTopicEffectRun(true)
    }, [article]);


    
    useEffect(() => {
        setCurrentSlide(allSlides[currentSlideIndex]);
        if (currentSlideIndex < elements.length) {
            elements[currentSlideIndex]!.classList.add(style.on__progress);
            goToSlide(currentSlideIndex);
        }
    }, [allSlides]);



    useEffect(() => {
        const handleAnimationEnd = () => {
            const nextIndex = currentSlideIndex < MAX_SLIDES ? currentSlideIndex + 1 : 0;
            goToSlide(nextIndex);
        };
    
        animatedDivRefs.current.forEach((el) => {
            if (el) {
                el.addEventListener(ANIMATION_END, handleAnimationEnd);
            }
        });
    
        return () => {
            animatedDivRefs.current.forEach((el) => {
                if (el) {
                    el.removeEventListener(ANIMATION_END, handleAnimationEnd);
                }
            });
        };
    }, [currentSlideIndex,isReady]);

    useEffect(() => {
        if (hasTopicEffectRun) {
            setIsReady(true);
        }
    }, [hasTopicEffectRun]);
    
    useEffect(() => {
        if (isReady) {
            goToSlide(0);
        }
    }, [isReady]);

    const goToSlide = (index: number) => {
        setCurrentSlide(allSlides[index]);
        setCurrentSlideIndex(index);
        initChanges(animatedDivRefs.current, index)
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === WINDOW_HIDDEN) {
                animatedDivRefs.current.forEach((el) => {
                    if (el) {
                        el.style.animationPlayState = ANIMATION_PAUSED;
                    }
                });
            } else if (document.visibilityState === WINDOW_VISIBLE) {
                animatedDivRefs.current.forEach((el) => {
                    if (el) {
                        el.style.animationPlayState = ANIMATION_RUN;
                    }
                });
            }
        };
    
        document.addEventListener(VISIBILITY_CHANGE, handleVisibilityChange);
    
        return () => {
            document.removeEventListener(VISIBILITY_CHANGE, handleVisibilityChange);
        };
    }, []);
    
    const initChanges = (elements: (HTMLDivElement | null)[], slideIndex: number): void => {
        switch (slideIndex) {
            case 0:
                elements[0]!.classList.add(style.on__progress);
                elements[0]!.classList.remove(style.completed);
                elements[1]!.classList.remove(style.completed);
                elements[1]!.classList.remove(style.on__progress);
                elements[1]!.style.width = NULL_PERCENT;
                elements[2]!.classList.remove(style.completed);
                elements[2]!.classList.remove(style.on__progress);
                elements[2]!.style.width = NULL_PERCENT;
                break;
            case 1:
                elements[0]!.classList.add(style.completed);
                elements[0]!.style.width = ONE_HUNDRED_PERCENT;
                elements[1]!.classList.remove(style.completed);
                elements[1]!.classList.add(style.on__progress);
                elements[2]!.classList.remove(style.completed);
                elements[2]!.classList.remove(style.on__progress);
                elements[2]!.style.width = NULL_PERCENT;
                break;

            case 2:
                elements[0]!.classList.add(style.completed);
                elements[0]!.style.width = ONE_HUNDRED_PERCENT;
                elements[1]!.classList.add(style.completed);
                elements[1]!.style.width = ONE_HUNDRED_PERCENT;
                elements[2]!.classList.add(style.on__progress);
                break;
            default:
                elements[0]!.classList.remove(style.completed);
                elements[0]!.classList.add(style.on__progress);
                elements[1]!.classList.remove(style.completed);
                elements[2]!.classList.remove(style.completed);
                break;
        }
    }

    return (
        <div className={style.article__newest}>
            <div className={style.article__newest__image}>
                {currentSlide ? (
                    <Link scroll={false} href={`/blog/${currentSlide.slug}`}>
                        <a>
                            {currentSlide.posterUrl ? (
                                <Image sizes="1024px" layout={'fill'} src={currentSlide.posterUrl} alt={currentSlide.header} />
                            ) : ''}
                        </a>
                    </Link>
                ) : null}
            </div>
            <div className={style.article__newest__body}>
                <div className={style.article__newest__title}>
                    {currentSlide && (
                        <Link scroll={false} href={`/blog/${currentSlide.slug}`}>
                            <a>
                                {currentSlide.header}
                            </a>
                        </Link>
                    )}
                </div>
                <div className={style.article__newest__subtitle} dangerouslySetInnerHTML={{
                    __html: currentSlide ? currentSlide.description : ''
                }}>
                </div>
                <div className={style.article__newest__reading__time}>
                    <ReadingTimeBlock readingTime={currentSlide ? currentSlide.readingTime : ''} articleLabel={currentSlide ? currentSlide.label : ''} />
                </div>
                <div className={style.article__newest__pagination}>
                    {allSlides.map((slide, index) => (
                        <div key={index} className={style.article__newest__pagination__btn} onClick={() => { goToSlide(index); }}>
                            <div className={style.article__newest__pagination__btn__animated} ref={el => animatedDivRefs.current[index] = el}>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
};

export default ArticlePreviewLarge;